/**
 * MiniStats Component
 * Stat cards with gradient background, icon, value, and label.
 */

import React from 'react'

const PhoneIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
)

const MailIcon: React.FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
)

export interface MiniStatItem {
    href: string
    theme?: string
    icon: string
    value?: number
    label: string
    sub?: string
}

export interface MiniStatsProps {
    stats: MiniStatItem[]
    animate?: boolean
    /** 'stats' (default) = numeric grid cards; 'contact' = contact info cards */
    variant?: 'stats' | 'contact'
}

const STAT_COLORS = ['blue', 'green', 'red', 'violet'] as const
const CONTACT_COLORS = ['blue', 'green', 'violet', 'rose'] as const

export const MiniStats: React.FC<MiniStatsProps> = ({ stats, animate = false, variant = 'stats' }) => {
    const fadeClass = animate ? 'animate-fade-in' : ''

    const renderIcon = (icon: string) => {
        const lower = icon.toLowerCase()
        if (lower === 'phone' || lower === 'fa-phone') return <PhoneIcon />
        if (lower === 'mail' || lower === 'email' || lower === 'fa-envelope') return <MailIcon />
        return <i className={`fa ${icon}`} aria-hidden />
    }

    if (variant === 'contact') {
        return (
            <div className="mini-stats-contact-grid">
                {stats.map((item, i) => {
                    const color = CONTACT_COLORS[i % CONTACT_COLORS.length]
                    return (
                        <a key={i} href={item.href} className={`mini-stat-contact-card mini-stat-contact-card--${color} group ${fadeClass}`}>
                            <div className={`mini-stat-contact-icon mini-stat-contact-icon--${color}`}>
                                {renderIcon(item.icon)}
                            </div>
                            <div className={`mini-stat-contact-label mini-stat-contact-label--${color}`}>{item.label}</div>
                            {item.sub && <div className={`mini-stat-contact-sub mini-stat-contact-sub--${color}`}>{item.sub}</div>}
                        </a>
                    )
                })}
            </div>
        )
    }

    return (
        <div className="mini-stats-grid">
            {stats.map((item, i) => {
                const color = STAT_COLORS[i % STAT_COLORS.length]
                return (
                    <a key={i} href={item.href} className={`mini-stat-card mini-stat-card--${color} ${fadeClass}`}>
                        <div className={`mini-stat-icon mini-stat-icon--${color}`}>
                            {renderIcon(item.icon)}
                        </div>
                        <div className="mini-stat-content">
                            <div className="mini-stat-value">
                                {item.value}
                                <span className="mini-stat-label">{item.label}</span>
                            </div>
                            {item.sub && <div className="mini-stat-sub">{item.sub}</div>}
                        </div>
                    </a>
                )
            })}
        </div>
    )
}

export default MiniStats
